import React from "react";
import { useNavigate } from "react-router-dom";
import Card from "./Card";
import { SOC_TILES } from "./socTileConfig";
import { formatAge, formatLocal, formatLocalDate, freshnessOf, staleSources } from "./socFreshness";
import { AlertTriangle } from "lucide-react";
import { buttonClass } from "./socUi";

// Freshness views for Security Operations (/soc): the stale-feed banner, the data connectors panel and
// the data quality checks. All three read the summary tiles' lastSyncAt through freshnessOf, so they
// always agree with each other and with the tiles' badges.

const titleOf = (tile) => SOC_TILES[tile.key]?.title || tile.key;

export const staleTiles = (tiles, now) =>
  (tiles || []).map((t) => ({ tile: t, f: freshnessOf(t, now) })).filter(({ f }) => f.state === "stale");

// One line per stopped connector of a stale area ("Email · Proofpoint: no new data since ..."), or one
// line for the area when it doesn't say which of its connectors stopped.
export const staleLines = (tile, f, now) => {
  const sources = staleSources(tile, now);
  if (sources.length === 0) {
    return [{ key: tile.key, area: titleOf(tile), connector: null, lastSync: f.lastSync, ageMs: f.ageMs }];
  }
  return sources.map((s) => ({ key: `${tile.key}-${s.name}`, area: titleOf(tile), connector: s.name, lastSync: s.lastSync, ageMs: s.ageMs }));
};

// Every stopped connector on the page: the lines of each stale area, and the stopped connectors of an
// area that is still current because another of its connectors is (Cloud keeps its newest source's
// time, so AWS stopping alone leaves the area fresh). Oldest first within an area, areas in page order.
export const stoppedConnectorLines = (tiles, now) =>
  (tiles || []).flatMap((tile) => {
    const f = freshnessOf(tile, now);
    if (f.state === "stale") return staleLines(tile, f, now);
    if (f.state === "notConnected") return [];
    return staleSources(tile, now).map((s) => ({
      key: `${tile.key}-${s.name}`, area: titleOf(tile), connector: s.name, lastSync: s.lastSync, ageMs: s.ageMs,
    }));
  });

const lineText = (l) => `${l.area}${l.connector ? ` · ${l.connector}` : ""}: no new data since ${formatLocalDate(l.lastSync)} (${formatAge(l.ageMs)})`;
const lineImpact = (l) => (l.connector ? `Figures from ${l.connector} may be out of date.` : "Its counts may be out of date.");

export const SocStaleBanner = ({ tiles, now, canConnect }) => {
  const navigate = useNavigate();
  const lines = stoppedConnectorLines(tiles, now);
  if (lines.length === 0) return null;
  return (
    <div
      role="status"
      data-testid="soc-stale-banner"
      className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-status-warning bg-status-warning-bg px-4 py-3 text-sm text-content-primary"
    >
      <div className="flex min-w-0 items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-status-warning" aria-hidden="true" />
        <div className="min-w-0">
        <p className="font-medium">
          {lines.length === 1 ? "1 connection has stopped sending data" : `${lines.length} connections have stopped sending data`}
        </p>
        <ul className="mt-1 space-y-0.5 text-content-secondary">
          {lines.map((l) => (
            <li key={l.key}>{`${lineText(l)}. ${lineImpact(l)}`}</li>
          ))}
        </ul>
        </div>
      </div>
      {canConnect && (
        <button
          type="button"
          onClick={() => navigate("/settings/integrations")}
          className={`shrink-0 ${buttonClass("md")}`}
        >
          Open data connectors
        </button>
      )}
    </div>
  );
};

const STATE_LABEL = {
  fresh: { label: "Feeding", className: "bg-status-success-bg text-status-success" },
  stale: { label: "Stale", className: "bg-status-warning-bg text-status-warning" },
  unknown: { label: "Sync time unknown", className: "bg-theme-secondary text-content-secondary" },
  notConnected: { label: "Not connected", className: "bg-theme-secondary text-content-muted" },
};

const STATE_ORDER = { stale: 0, unknown: 1, fresh: 2, notConnected: 3 };

export const SocConnectorsPanel = ({ tiles, now }) => {
  const rows = (tiles || [])
    .map((t) => ({ tile: t, f: freshnessOf(t, now) }))
    .sort((a, b) => STATE_ORDER[a.f.state] - STATE_ORDER[b.f.state]);
  const feeding = rows.filter((r) => r.f.state === "fresh").length;
  const connected = rows.filter((r) => r.f.state !== "notConnected").length;
  return (
    <Card>
      <section aria-label="Data connectors" data-testid="soc-connectors" id="soc-connectors" className="scroll-mt-4">
        <h2 className="text-lg font-semibold text-content-primary">Data connectors</h2>
        <p className="text-xs text-content-muted">
          {feeding} of {connected} connected areas are receiving data
        </p>
        <ul className="mt-3 divide-y divide-border">
          {rows.map(({ tile, f }) => {
            const state = STATE_LABEL[f.state];
            return (
              <li key={tile.key} className="-mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-theme-hover">
                <span className="min-w-0 text-content-primary">
                  {titleOf(tile)}
                  {/* A connector that stopped while the area is still fed by another, as the banner names it. */}
                  {f.state === "fresh" && staleSources(tile, now).map((s) => (
                    <span key={s.name} className="ml-2 text-xs text-status-warning" data-testid={`soc-connectors-stopped-${tile.key}`}>
                      {`${s.name} stopped ${formatAge(s.ageMs)} ago`}
                    </span>
                  ))}
                </span>
                <span className="flex items-center gap-2">
                  {f.lastSync && (
                    <span className="text-xs text-content-muted" title={formatLocal(f.lastSync)}>
                      {formatAge(f.ageMs)} ago
                    </span>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${state.className}`}>{state.label}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </Card>
  );
};

// Rules over data the page already has. Each check states a fact and why it matters; none guesses.
export const dataQualityChecks = (tiles, now) => {
  const byKey = Object.fromEntries((tiles || []).map((t) => [t.key, t]));
  const checks = [];
  stoppedConnectorLines(tiles, now).forEach((l) => checks.push({
    key: `stale-${l.key}`,
    text: `${l.area}${l.connector ? ` (${l.connector})` : ""} has received no new data since ${formatLocalDate(l.lastSync)} (${formatAge(l.ageMs)}). ${
      l.connector ? `Figures from ${l.connector} may be out of date.` : "Its counts and status may be out of date."}`,
  }));
  (tiles || []).forEach((t) => {
    if (freshnessOf(t, now).state === "unknown") {
      checks.push({ key: `unknown-${t.key}`, text: `${titleOf(t)} is connected but reports no sync time, so its freshness can't be checked.` });
    }
  });
  // Devices against the people who would use them: identities less non-human ones (service accounts,
  // apps) and guests, which have no device of their own in the organization.
  const devices = byKey.endpoint?.metrics?.devices;
  const idm = byKey.identity?.metrics || {};
  const people = Number.isFinite(idm.identities)
    ? idm.identities - (Number(idm.nonHuman) || 0) - (Number(idm.guests) || 0)
    : null;
  if (Number.isFinite(devices) && people > 0 && devices < people) {
    checks.push({
      key: "endpoint-coverage",
      text: `${devices} ${devices === 1 ? "device reports" : "devices report"} to endpoint protection against ${people} ${people === 1 ? "user account" : "user accounts"} (identities other than non-human and guest accounts). Unless most people share devices, some are not onboarded, so endpoint counts understate the real exposure.`,
    });
  }
  return checks;
};

export const SocDataChecks = ({ tiles, now }) => {
  const checks = dataQualityChecks(tiles, now);
  return (
    <Card>
      <section aria-label="Data quality checks" data-testid="soc-data-checks">
        <h2 className="text-lg font-semibold text-content-primary">Data quality checks</h2>
        {checks.length === 0 ? (
          <p className="mt-2 text-sm text-content-secondary">No data quality issues found.</p>
        ) : (
          <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm text-content-secondary">
            {checks.map((c) => <li key={c.key}>{c.text}</li>)}
          </ol>
        )}
      </section>
    </Card>
  );
};
